import { useContext } from 'react';
import { PageContext } from 'src/context/PageContext';
import { ContentComponent, ContentType, MapLayer, Translations } from 'src/types/publishedAssets';
import { decodeHtmlEntities } from './decodeHtmlEntities';

/**
 * Content keys whose every consumer renders them as React text, so a resolved value can be
 * entity-decoded (ENG-4305). A write-path sanitizer entity-encoded these fields for ten
 * days in July 2026, which is why residents see `Parks &amp; Recreation`. The stored rows
 * are left exactly as they are; the repair happens here, at the point of display.
 *
 * The allowlist is the safety mechanism, not a convenience: React escapes text on output,
 * so decoding into a text node cannot produce markup. Two kinds of key are excluded.
 * `support_text` and `body_text` reach `marked` and `dangerouslySetInnerHTML`, where
 * decoding stored `&lt;script&gt;` would emit a live script into the server-rendered
 * document, and they need no repair anyway because the browser decodes markdown output.
 * `description` is excluded because it has BOTH sinks: plain text in Document, markdown in
 * ProfileCards, FeedItemCard and the map layer panel. A key that is not uniformly a text
 * sink cannot be gated by key alone.
 *
 * Anything not listed is returned untouched. Add a key only after checking every consumer
 * of it, not just the one in front of you.
 */
const PLAIN_TEXT_KEYS = new Set([
  'heading',
  'title',
  'label',
  'button_label',
  'button_text',
  'name',
  'subtitle',
  'job_title',
  'social_tagline',
  'timeframe',
  'text',
  'alt_text',
]);

const resolvePlainText = (key: string, value: string): string =>
  PLAIN_TEXT_KEYS.has(key) ? decodeHtmlEntities(value) : value;

export function useGetContentTranslations() {
  const { page, questionnaire, urlParams } = useContext(PageContext);
  const currentLocaleCode = urlParams?.locale as string;
  const defaultLocaleCode = (page?.default_locale_code ?? questionnaire?.default_locale_code) as string;

  const translate = (translations: Translations | undefined, key: string) =>
    resolvePlainText(key, translations?.[currentLocaleCode]?.[key] || translations?.[defaultLocaleCode]?.[key] || '');

  // Falls back to any other locale that has the field so a slug/tab derived from this
  // (see determineSectionId, getSectionTabs) stays stable even when a section is only
  // translated in a non-default locale, instead of being treated as untitled.
  //
  // Deliberately NOT entity-decoded, unlike `translate`. Its output feeds
  // `determineSectionId`, which is the section's anchor and tab slug, so decoding here
  // would rewrite the URL of every section whose label holds an entity. Display is already
  // covered: `translate` falls back to the default locale and is decoded.
  const translateDefault = (translations: Translations | undefined, key: string) => {
    if (translations?.[defaultLocaleCode]?.[key]) {
      return translations[defaultLocaleCode][key];
    }
    const fallbackLocale = Object.keys(translations ?? {}).find((locale) => translations?.[locale]?.[key]);
    return (fallbackLocale && translations?.[fallbackLocale]?.[key]) || '';
  };

  /**
   * Alt text carries the same entity damage as the translation fields, but reaches
   * components through `media_item` rather than `translate`, so it needs repairing here
   * too. Without this the same field renders decoded in ProfileCards (which goes through
   * `translate`) and encoded in Hero, Document and the galleries. Only `alt_text` is
   * touched; `url` is left exactly as stored.
   */
  const withDecodedAltText = <T extends { alt_text?: string } | null>(mediaItem: T): T =>
    mediaItem?.alt_text ? { ...mediaItem, alt_text: decodeHtmlEntities(mediaItem.alt_text) } : mediaItem;

  const getMediaItem = (content: ContentType, index: number) =>
    withDecodedAltText(
      content?.components?.[index]?.media_item?.[currentLocaleCode] ||
        content?.components?.[index]?.media_item?.[defaultLocaleCode] ||
        null,
    );

  const getImageCrop = (content: ContentType, index: number) => content?.components?.[index]?.metadata?.imageCrop;

  const getImageCropFromComponent = (component: ContentComponent) => component?.metadata?.imageCrop;

  const getMediaItemFromComponent = (component: ContentComponent | MapLayer) =>
    withDecodedAltText(
      component?.media_item?.[currentLocaleCode] || component?.media_item?.[defaultLocaleCode] || null,
    );

  const getMediaItemFromComponentKey = (component: ContentComponent | MapLayer, key: 'url' | 'alt_text') => {
    const value =
      component?.media_item?.[currentLocaleCode]?.[key] || component?.media_item?.[defaultLocaleCode]?.[key];
    return (key === 'alt_text' && value ? decodeHtmlEntities(value) : value) || null;
  };

  return {
    translate,
    translateDefault,
    getMediaItem,
    getImageCrop,
    getImageCropFromComponent,
    getMediaItemFromComponent,
    getMediaItemFromComponentKey,
  };
}
